import { useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '@/context/AdminContext';

const footerLinks = [
  {
    title: 'Navegação',
    links: [
      { label: 'Início', href: '/' },
      { label: 'Portfólio', href: '/portfolio' },
    ],
  },
  {
    title: 'Serviços',
    links: [
      { label: 'Casamentos', href: '/portfolio?categoria=casamentos' },
      { label: 'Pré-Wedding', href: '/portfolio?categoria=pre-wedding' },
      { label: 'Celebração', href: '/portfolio?categoria=celebracao' },
      { label: 'Sessão Família', href: '/portfolio?categoria=sessao-familia' },
    ],
  },
  {
    title: 'Contato',
    links: [
      { label: 'Solicitar Orçamento', href: '/contato' },
      { label: 'Depoimentos', href: '/#depoimentos' },
    ],
  },
  {
    title: 'Redes Sociais',
    links: [
      { label: 'Instagram', href: 'https://www.instagram.com/jessicaboninifotografa', external: true },
    ],
  },
];

export default function Footer() {
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const [clickCount, setClickCount] = useState(0);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLogoClick = useCallback(() => {
    const newCount = clickCount + 1;
    setClickCount(newCount);

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }

    if (newCount >= 5) {
      setClickCount(0);
      if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
      // If already logged in, go straight to admin portfolio
      navigate(isAdmin ? '/admin/portfolio' : '/admin/login');
      return;
    }

    clickTimerRef.current = setTimeout(() => {
      setClickCount(0);
    }, 2000);
  }, [clickCount, navigate, isAdmin]);

  return (
    <footer className="bg-cream">
      <div className="px-6 md:px-10 lg:px-16 pt-16 pb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12 mb-16">
          {footerLinks.map((section) => (
            <div key={section.title}>
              <h4 className="text-sm font-semibold text-dark mb-4 tracking-wide uppercase">
                {section.title}
              </h4>
              <div className="h-[1px] w-8 bg-warm-400 mb-4" />
              <ul className="flex flex-col gap-2.5">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-dark/60 hover:text-dark transition-colors duration-300"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-dark/60 hover:text-dark transition-colors duration-300"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-warm-200 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Secret admin trigger: click 5 times on logo */}
          <button
            onClick={handleLogoClick}
            className="flex flex-col items-start text-left cursor-pointer select-none"
            type="button"
            aria-label="Jéssica Bonini Fotógrafa"
          >
            <span className="font-serif text-xl font-semibold text-dark leading-tight">
              Jéssica Bonini
            </span>
            <span className="text-xs tracking-[0.2em] uppercase text-dark/60">
              Fotógrafa
            </span>
          </button>

          <p className="text-xs text-dark/50 text-center">
            © 2026 Jéssica Bonini Fotógrafa. Todos os direitos reservados.
          </p>

          <div className="flex items-center gap-4">
            <a
              href="https://www.instagram.com/jessicaboninifotografa"
              target="_blank"
              rel="noopener noreferrer"
              className="w-9 h-9 flex items-center justify-center rounded-full bg-warm-100 text-dark/60 hover:bg-dark hover:text-white transition-all duration-300"
              aria-label="Instagram"
            >
              <i className="ri-instagram-line text-sm" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}